import clsx from "clsx";
import { motion } from "framer-motion";
import React, { FC, useMemo, useState } from "react";

import { useBreakpoints } from "../../../hooks/use-breakpoints";
import { Fade } from "../../animations/fade";
import { IconButton } from "../../buttons/icon-button";
import { AnimatedSliderProps } from "./animated-slider";
import { AnimatedSliderItem } from "./animated-slider-item";
import { AnimatedSliderVariantCustom, mobileSlideVariants, slideVariants } from "./slide-variants";
import { AnimatedSliderState } from "./types";

export type AnimatedSliderInnerProps = Pick<
  AnimatedSliderProps,
  "items" | "variant" | "maxVisibleCards"
> & {
  /**
   * Current animation state
   */
  animationState: AnimatedSliderState;
  /**
   * Callback when animation is complete
   */
  onAnimationComplete?: () => void;
  /**
   * Whether is mobile version
   */
  isMobile?: boolean;
};

export const AnimatedSliderInner: FC<AnimatedSliderInnerProps> = ({
  items,
  animationState,
  onAnimationComplete,
  isMobile,
  maxVisibleCards,
  variant,
}) => {
  const [prevIndex, setPrevIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const breakpoints = useBreakpoints();

  const animationDuration = Math.max(0.3 * Math.abs(prevIndex - activeIndex), 0.4);
  const animationDelay = animationDuration / 30;

  const visibleCards = useMemo(() => {
    let responsiveMaxVisibleCards = 1;
    if (breakpoints.sm) responsiveMaxVisibleCards = 2;
    if (breakpoints.lg) responsiveMaxVisibleCards = 3;
    if (breakpoints["2xl"]) responsiveMaxVisibleCards = 4;

    if (maxVisibleCards === null) return responsiveMaxVisibleCards;
    return Math.min(responsiveMaxVisibleCards, maxVisibleCards);
  }, [breakpoints, maxVisibleCards]);

  const hasPrevious = activeIndex > 0;
  const hasNext = activeIndex < items.length - visibleCards;
  const isFinalState = animationState === AnimatedSliderState.FINAL;

  const handlePrev = () => {
    if (hasPrevious) {
      setDirection(-1);
      setPrevIndex(activeIndex);
      setActiveIndex((currIndex) => Math.max(0, currIndex - visibleCards));
    }
  };

  const handleNext = () => {
    if (hasNext) {
      setDirection(1);
      setPrevIndex(activeIndex);
      setActiveIndex((currIndex) =>
        Math.min(currIndex + visibleCards, items.length - visibleCards)
      );
    }
  };

  const arrowPosition =
    items?.[0]?.__typename === "ContentfulFlatCard" ? "top-[200px] 2xl:top-[230px]" : "top-1/2";

  return (
    <div className="block w-full overflow-visible relative">
      <div className="flex gap-8">
        {items.map((item, index) => (
          <motion.div
            key={index}
            variants={isMobile ? mobileSlideVariants : slideVariants}
            custom={
              {
                itemIndex: index,
                activeIndex,
                animationState,
                visibleCards,
              } satisfies AnimatedSliderVariantCustom
            }
            initial="initial"
            animate="animate"
            onAnimationComplete={onAnimationComplete}
            transition={{
              duration: animationDuration,
              ease: "easeInOut",
              delay:
                direction > 0
                  ? Math.max(0, animationDelay * (visibleCards + index - activeIndex))
                  : Math.max(0, animationDelay * (prevIndex + visibleCards - index - 1)),
            }}
            className={clsx([
              // The space occupied by the card is 100%/number of cards, leaving its margin on the right (32px).
              // The last card though doesn't have that margin so the space occupied
              // by every card should be increased evenly by the space gained deleting the missing margin
              visibleCards === 1 && "flex-[0_0_100%]",
              visibleCards === 2 && "flex-[0_0_calc((100%)/2-(32px/2))]",
              visibleCards === 3 && "flex-[0_0_calc((100%)/3-(64px/3))]",
              visibleCards === 4 && "flex-[0_0_calc((100%)/4-(96px/4))]",
              isFinalState ? "pointer-events-auto" : "pointer-events-none",
            ])}
          >
            <AnimatedSliderItem
              {...{ item, variant, animationState, visibleCards }}
              isFullScreen={(maxVisibleCards ?? 0) === 1}
            />
          </motion.div>
        ))}
      </div>
      <Fade visible={isFinalState && hasPrevious}>
        <IconButton
          className={clsx("absolute left-2 lg:left-0 translate-x-[-50%] z-30", arrowPosition)}
          icon="arrow-left"
          variant="light"
          onClick={handlePrev}
        />
      </Fade>
      <Fade visible={isFinalState && hasNext}>
        <IconButton
          className={clsx("absolute right-2 lg:right-0 translate-x-[50%] z-30", arrowPosition)}
          icon="arrow-right"
          variant="light"
          onClick={handleNext}
        />
      </Fade>
    </div>
  );
};
